import { Arsenal } from "./arsenal";
import type { Board } from "./board";
import { Inventory } from "./inventory";
import { DamageType } from "./damageType";
import { ALL_DIRECTIONS, Direction, directionDelta, directionFromVector, reverseDirection } from "./direction";
import { Event } from "./events/event";
import { EventType } from "./events/eventType";
import {
	PRIORITY_FIRST,
	type ProcessableThing,
	type LookAtHorizontalDirection,
	type ParticleEmitter,
	type Bumpable,
	type Interactable,
	isBumpable,
	isInteractable,
	isRechargeable,
} from "./interfaces";
import { emitBloodEffect } from "./emitBloodEffect";
import { PhysicalThingWithHp, STD_BASE_HP } from "./physicalThingWithHp";
import { PlayerCommand, getMoveDirection, getShootDirection } from "./playerCommand";
import { randomElement } from "./random";
import { deserializeWeapon, deserializeXY } from "./serialization";
import type { PhysicalThing } from "./physicalThing";
import type { Thing } from "./thing";
import { ArtilleryAimIndicator } from "./things/artilleryAimIndicator";
import { Sound } from "./things/sounds/sound";
import { SoundId } from "./things/sounds/soundId";
import { Tomb } from "./things/tomb";
import type { Weapon } from "./weapon";
import { XY } from "./xy";
import { MovementEffect } from "./effects/movementEffect";
import { ParticleEffect } from "./effects/particleEffect";
import { GoldPickupEffect } from "./effects/goldPickupEffect";
import { ParticleType } from "./particleType";
import { GoldDrop } from "./things/goldDrop";
import { Medikit } from "./items/medikit";
import { NightVisionEquipment } from "./items/nightVisionEquipment";
import { Mob } from "./mob";
import { MultiTileThingPart } from "./things/multitile/multiTileThingPart";
import { MiningRobot } from "./things/miningRobot";
import { ArtilleryRobot } from "./things/artilleryRobot";
import { Portal2 } from "./things/portal2/portal2";
import { Portal2Part } from "./things/portal2/portal2Part";
import { RobotSpawner } from "./things/robotSpawner";
import { BlackHoleProjectile } from "./projectiles/blackHoleProjectile";
import { ZOrder } from "./zOrder";

export class Player
	extends PhysicalThingWithHp
	implements ProcessableThing, LookAtHorizontalDirection, ParticleEmitter
{
	static key = "Player";
	pendingCommand: PlayerCommand | null = null;
	pendingActionItemKey: string | null = null;
	arsenal: Arsenal = new Arsenal();
	inventory: Inventory = new Inventory();
	lookDirection: Direction = Direction.E;
	damageReceivedThisTurn: number = 0;
	goldCollectedThisTurn: number = 0;
	gold: number = 0;
	isJumping: boolean = false;
	isInvulnerable: boolean = false;
	private autoMoving: boolean = false;
	private firedThisTurn: boolean = false;

	constructor(xy: XY) {
		super(xy, true);
		this.hp = STD_BASE_HP * 6;
		this.maxHp = STD_BASE_HP * 6;
		this.damageMultipliers.set(DamageType.TOXIC_GAS, 0.8);
		this.damageMultipliers.set(DamageType.FIRE, 4);
	}

	getSelectedWeapon(): Weapon | null {
		return this.arsenal.getSelectedWeapon();
	}

	selectNextWeapon(): void {
		this.arsenal.selectNextWeapon();
	}

	selectPreviousWeapon(): void {
		this.arsenal.selectPreviousWeapon();
	}

	selectWeaponByIndex(index: number): void {
		this.arsenal.selectWeaponByIndex(index);
	}

	selectWeaponFamily(familyId: number): void {
		this.arsenal.selectFamily(familyId);
	}

	addWeaponToArsenal(weapon: Weapon): void {
		weapon.owner = this;
		this.arsenal.addWeapon(weapon);
	}

	setAutoMoving(value: boolean): void {
		this.autoMoving = value;
	}

	isAutoMoving(): boolean {
		return this.autoMoving;
	}

	findAutoShootDirection(board: Board): Direction | null {
		const weapon = this.getSelectedWeapon();
		if (!weapon || !weapon.canAutoFire()) return null;

		const nearest = this.findClosestHostile(board);
		if (nearest === null) return null;
		// Range only gates whether to fire — it never picks the direction.
		if (!this.xy.isInSquareRadius(nearest, weapon.getAutoFireRange())) return null;

		return this.findBestInLineDirection(board) ?? Player.bestAlignedDirection(this.xy, nearest);
	}

	private findBestInLineDirection(board: Board): Direction | null {
		const maxRange = board.width + board.height;
		const target = ALL_DIRECTIONS.map((dir) => this.findClosestHostileOnRay(dir, board, maxRange))
			.filter((t) => t !== null)
			.sort((a, b) => {
				// Within 1 tile, an unfrozen target beats a temp-frozen one; otherwise closest wins.
				if (a.adjacent && b.adjacent && a.isUnfrozen !== b.isUnfrozen) return a.isUnfrozen ? -1 : 1;
				return a.distSq - b.distSq;
			})[0];
		return target?.dir ?? null;
	}

	private findClosestHostileOnRay(
		dir: Direction,
		board: Board,
		maxRange: number,
	): { dir: Direction; distSq: number; adjacent: boolean; isUnfrozen: boolean } | null {
		const delta = directionDelta(dir);
		for (let step = 1; step <= maxRange; step++) {
			const cellXY = this.xy.add(delta.x * step, delta.y * step);
			if (!board.isInBounds(cellXY)) return null;
			const tier = Player.evaluateCellHostility(board, cellXY);
			if (tier === null) continue;
			return {
				dir,
				distSq: cellXY.distanceSquared(this.xy),
				adjacent: step === 1,
				isUnfrozen: tier === "unfrozen",
			};
		}
		return null;
	}

	private findClosestHostile(board: Board): XY | null {
		let closest: XY | null = null;
		let closestDistSq = Infinity;
		for (const thing of board.getPhysicalThings()) {
			if (Player.thingHostility(thing) === null) continue;
			const distSq = thing.xy.distanceSquared(this.xy);
			if (distSq < closestDistSq) {
				closestDistSq = distSq;
				closest = thing.xy;
			}
		}
		return closest;
	}

	// The direction whose ray passes nearest the target (max dot = min perpendicular distance).
	private static bestAlignedDirection(from: XY, to: XY): Direction {
		const vx = to.x - from.x;
		const vy = to.y - from.y;
		let best = ALL_DIRECTIONS[0];
		let bestDot = -Infinity;
		for (const dir of ALL_DIRECTIONS) {
			const delta = directionDelta(dir);
			const dot = (delta.x * vx + delta.y * vy) / Math.hypot(delta.x, delta.y);
			if (dot > bestDot) {
				bestDot = dot;
				best = dir;
			}
		}
		return best;
	}

	private static evaluateCellHostility(board: Board, xy: XY): "unfrozen" | "tempFrozen" | null {
		let tempFrozenSeen = false;
		for (const thing of board.getThingsAt(xy)) {
			const tier = Player.thingHostility(thing);
			if (tier === "unfrozen") return "unfrozen";
			if (tier === "tempFrozen") tempFrozenSeen = true;
		}
		return tempFrozenSeen ? "tempFrozen" : null;
	}

	private static thingHostility(thing: PhysicalThing): "unfrozen" | "tempFrozen" | null {
		if (!thing.active) return null;
		if (thing instanceof Mob) {
			if (thing.isPermanentlyFrozen()) return null;
			if (thing.isFrozen()) return "tempFrozen";
			return "unfrozen";
		}
		if (thing instanceof MultiTileThingPart) {
			const core = thing.controller.getThing();
			if (core instanceof MiningRobot || core instanceof ArtilleryRobot) return "unfrozen";
		}
		if (thing instanceof Portal2 || thing instanceof Portal2Part || thing instanceof RobotSpawner) return "unfrozen";
		return null;
	}

	isInDanger(): boolean {
		if (!this.board) return false;
		const board = this.board;
		if (this.xy.getAreaInSquareRadius(3).some((xy) => BlackHoleProjectile.blocksCell(board, xy))) return true;
		for (let dx = -2; dx <= 2; dx++) {
			for (let dy = -2; dy <= 2; dy++) {
				const evalXY = this.xy.add(new XY(dx, dy));
				if (!evalXY.isInside(this.board)) continue;
				if (this.board.hasMob(evalXY)) return true;
				if (this.board.getThingOfClass(ArtilleryAimIndicator, evalXY) !== null) return true;
			}
		}
		return false;
	}

	setCommand(cmd: PlayerCommand): void {
		this.pendingCommand = cmd;
	}

	setUseItemCommand(itemKey: string): void {
		this.pendingCommand = PlayerCommand.USE_ITEM;
		this.pendingActionItemKey = itemKey;
	}

	getLookingAtHorizontalDirection(): Direction {
		return this.lookDirection;
	}

	canSeeInDark(): boolean {
		return this.inventory.getItemOfClass(NightVisionEquipment)?.enabled === true;
	}

	getZOrder(): number {
		return ZOrder.PLAYER;
	}

	// A body never seals a tile against gas — gas spreads onto the player.
	override isGasPermeable(): boolean {
		return true;
	}

	getParticleType(): ParticleType | null {
		if (this.isJumping) return ParticleType.JETPACK_FLAME;
		return this.hp < this.maxHp * 0.6 ? ParticleType.BLOOD_DRIP : null;
	}

	getParticleIntensity(): number {
		if (this.isJumping) return 1.0;
		return 1 - this.hp / this.maxHp;
	}

	getProcessPriority(): number {
		return PRIORITY_FIRST;
	}

	processTurn(board: Board): void {
		this.firedThisTurn = false;
		if (this.pendingCommand === null) {
			return;
		}
		this.firedThisTurn =
			getShootDirection(this.pendingCommand) !== null || this.pendingCommand === PlayerCommand.AUTO_SHOOT;

		if (this.isJumping) {
			this.isJumping = false;
			board.addEffect(new ParticleEffect(this.xy, ParticleType.STEP_DUST, 12));
		}

		const startXY = this.xy;
		let shootDir = getShootDirection(this.pendingCommand);

		if (this.pendingCommand === PlayerCommand.AUTO_SHOOT) {
			const autoDir = this.findAutoShootDirection(board);
			if (autoDir === null) {
				board.turnAbortedByPlayer = true;
			} else {
				shootDir = autoDir;
			}
		}

		const moveDir = getMoveDirection(this.pendingCommand);
		if (moveDir !== null) {
			const delta = directionDelta(moveDir);
			const target = this.xy.add(delta);
			const oldXY = this.xy;
			if (board.isInBounds(target) && !board.hasMob(target)) {
				if (!board.isPassable(target)) {
					const bumpables = board.getThingsAt(target).filter((t) => isBumpable(t)) as unknown as Bumpable[];
					if (bumpables.length > 0) {
						let successes = 0;
						for (const b of bumpables) {
							if (b.bump(reverseDirection(moveDir), this)) successes++;
						}
						if (successes > 0) {
							board.bumpActionOccurred = true;
							if (board.isPassable(target)) {
								this.xy = target;
							}
						}
					}
				} else {
					this.xy = target;
				}
			}
			if (!oldXY.equals(this.xy)) {
				board.addEffect(new MovementEffect(this, oldXY, this.xy));
				board.addEffect(new ParticleEffect(oldXY, ParticleType.STEP_DUST, 1));
				board.stats.recordTileWalked();
			}
		}

		if (shootDir !== null) {
			// Grab gold before firing so anything dropped by this shot stays visible until next turn.
			this.grabNearbyGold(board);
			this.getSelectedWeapon()?.pullTrigger(shootDir, board);
		}

		if (this.pendingCommand === PlayerCommand.INTERACT) {
			const candidates: { thing: Interactable; xy: XY }[] = [];
			for (const neighborXY of this.xy.getNeighbors()) {
				if (!board.isInBounds(neighborXY)) continue;
				for (const t of board.getThingsAt(neighborXY)) {
					if (isInteractable(t) && t.canInteract()) {
						candidates.push({ thing: t, xy: neighborXY });
					}
				}
			}
			const picked = randomElement(candidates);
			if (picked === null) {
				board.interactionFailed = true;
			} else {
				picked.thing.interact(this);
				const dx = picked.xy.x - this.xy.x;
				if (dx !== 0) {
					this.lookDirection = dx > 0 ? Direction.E : Direction.W;
				}
			}
		}

		if (this.pendingCommand === PlayerCommand.USE_ITEM && this.pendingActionItemKey !== null) {
			this.handleUseItem(this.pendingActionItemKey, board);
		}

		const shotWithoutMoving = shootDir !== null && startXY.equals(this.xy);
		if (!shotWithoutMoving) {
			this.grabNearbyGold(board);
		}

		this.lookDirection = this.calculateLookDirection(shootDir, moveDir);

		this.pendingCommand = null;
		this.pendingActionItemKey = null;
	}

	private grabNearbyGold(board: Board): void {
		const radius = GoldDrop.GRAB_RADIUS;
		let totalGrabbed = 0;

		for (const xy of this.xy.getAreaInSquareRadius(radius)) {
			if (!board.isInBounds(xy)) continue;
			for (const thing of board.getThingsAt(xy)) {
				if (thing instanceof GoldDrop) {
					this.gold += thing.amount;
					board.stats.recordGoldCollected(thing.amount);
					totalGrabbed += thing.amount;
					if (!thing.xy.equals(this.xy)) {
						board.addEffect(new MovementEffect(thing, thing.xy, this.xy));
					}
					board.removeAtEndOfTurn(thing);
				}
			}
		}

		if (totalGrabbed > 0) {
			board.addNonPhysicalThing(new Sound(SoundId.PICKUP_GOLD));
			this.goldCollectedThisTurn += totalGrabbed;
			board.addEffect(new GoldPickupEffect(this.xy, totalGrabbed));
		}
	}

	applyDamage(type: DamageType | null, amount: number, source: Thing | null): number {
		if (this.isInvulnerable) return 0;
		this.setAutoMoving(false);
		const dmg = super.applyDamage(type, amount, source);
		this.damageReceivedThisTurn += dmg;
		if (dmg > 0 && this.board) {
			const sourceKey = source ? (source.constructor as any).key : "Unknown";
			this.board.stats.recordDamageReceived(sourceKey, dmg);
			this.board.stats.recordHpSnapshot(this.hp);

			const sound = new Sound(SoundId.PLAYER_HURT);
			sound.randomPitchVariation = 0.05;
			this.board.addNonPhysicalThing(sound);

			emitBloodEffect(this.board, this.xy, ParticleType.BLOOD, type, source);

			this.maybeEmitMedikitHint();
		}
		return dmg;
	}

	private maybeEmitMedikitHint(): void {
		const game = this.board?.game;
		if (!game || game.gameFlags.medikitHintShown) return;
		if (this.hp <= 0) return;
		if (this.hp / this.maxHp >= 0.6) return;
		if (!this.inventory.getItemOfClass(Medikit)) return;
		game.gameFlags.medikitHintShown = true;
		this.board!.addEvent(new Event(EventType.LOW_HP_MEDIKIT_HINT));
	}

	resetDamageTracking(): void {
		this.damageReceivedThisTurn = 0;
		this.goldCollectedThisTurn = 0;
	}

	protected onZeroHp(source: Thing | null, type: DamageType | null): void {
		super.onZeroHp(source, type);
		if (this.board?.game) {
			const sourceKey = source ? (source.constructor as any).key : "Unknown";
			const dmgType = type !== null ? type : "Unknown";
			this.board.game.stats.recordCauseOfDeath(sourceKey, String(dmgType));
		}
		this.board!.addThing(new Tomb(this.xy));
		this.board!.addEvent(new Event(EventType.PLAYER_DIED));
	}

	// Called by Board.processTurn once the turn is known to be consumed. Recharge advances
	// on every consumed turn except the firing turn itself.
	onTurnConsumed(): void {
		if (this.firedThisTurn) return;
		const weapon = this.getSelectedWeapon();
		if (isRechargeable(weapon)) weapon.tickCooldown();
	}

	private handleUseItem(itemKey: string, board: Board): void {
		const item = this.inventory.getItemByKey(itemKey);
		if (!item) {
			board.turnAbortedByPlayer = true;
			return;
		}
		const result = item.use(this, board);
		if (result.consumed) {
			this.inventory.consumeOne(item.constructor as any);
		}
		if (!result.turnPassed) {
			board.turnAbortedByPlayer = true;
		}
	}

	private calculateLookDirection(shootDir: Direction | null, moveDir: Direction | null): Direction {
		const actionDir = shootDir ?? moveDir;
		if (actionDir === null) return this.lookDirection;

		const delta = directionDelta(actionDir);
		if (delta.x === 0) return this.lookDirection;

		return directionFromVector(new XY(delta.x, 0)) ?? this.lookDirection;
	}

	serialize(): Record<string, unknown> {
		return {
			...super.serialize(),
			lookDirection: this.lookDirection,
			arsenal: this.arsenal.serialize(),
			inventory: this.inventory.serialize(),
			gold: this.gold,
			isInvulnerable: this.isInvulnerable,
		};
	}

	static deserialize(data: any): Player {
		const player = new Player(deserializeXY(data.xy));
		player.restoreBaseFields(data);
		player.lookDirection = data.lookDirection ?? Direction.E;

		if (Array.isArray(data.arsenal)) {
			// Old save format: arsenal is a plain weapon array
			const weapons = data.arsenal.map((w: any) => deserializeWeapon(w, player));
			for (const weapon of weapons) {
				player.arsenal.addWeapon(weapon);
			}
			const flatIndex = data.selectedWeaponIndex ?? 0;
			player.arsenal.selectWeaponByIndex(flatIndex);
		} else {
			player.arsenal = Arsenal.deserialize(data.arsenal, player);
		}

		player.inventory = data.inventory ? Inventory.deserialize(data.inventory) : new Inventory();
		player.gold = data.gold ?? 0;
		player.isInvulnerable = data.isInvulnerable ?? false;

		return player;
	}
}
